import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AuthService} from '@app/auth/auth.service';
import {deserialize, deserializeArray} from '@app/serializable';
import {StorageService} from '@app/shared/storage.service';
import {User} from '@app/shared/user.service';

import {mergeMap, Observable, ReplaySubject, Subject} from 'rxjs';
import {map, share, tap} from 'rxjs/operators';

export class Invitation {
    id: string;
    email: string;
    creator: string;
    client: string;
    expires: string;
    url: string;
    user: any;
    role: number;

    expired(): boolean {
        return this.expires < new Date().toISOString();
    }
}

export class Client {
    id: string;
    name: string;
    created: Date;
    schema_name: string;
    domain: any;
    owner_id: string;
    config: any;
    members: any[];
    paid_members: number;
    member_quota: number;
    storage_quota: number;
    storage_use: number;
    setup: boolean;
    is_paid: boolean;
    cancel_at: Date;
    invitations: Invitation[];
}

@Injectable({
    providedIn: 'root',
})
export class ClientService {
    public client: ReplaySubject<Client>;
    public clients$: ReplaySubject<Client[]>;

    private clientObservable: Observable<Client>;
    private clientsObservable: Subject<Client[]>;

    constructor(
        private storageService: StorageService,
        private authService: AuthService,
        private http: HttpClient,
    ) {
        this.client = new ReplaySubject<Client>();
        this.clients$ = new ReplaySubject<Client[]>();
        this.clientObservable = null;
        this.clientsObservable = null;
    }

    get(force: boolean = false): Observable<Client> {
        if (this.clientObservable === null || force) {
            this.clientObservable = this.http.get('/api/workspace/client/client/').pipe(
                map((result) => {
                    const client = deserialize(Client, result);
                    if (client.invitations) {
                        const invitations = client.invitations.filter(
                            (invitation) => invitation.email !== null,
                        );
                        client.invitations = deserializeArray(Invitation, invitations || []);
                    }
                    return client;
                }),
                tap((client) => this.client.next(client)),
                share(),
            );
        }
        return this.clientObservable;
    }

    getClients(): Observable<Client[]> {
        this.http
            .get<Client[]>('/api/workspace/client/')
            .pipe(map((clients) => deserializeArray(Client, clients)))
            .subscribe((clients) => this.clients$.next(clients));

        return this.clients$;
    }

    setClient(clientId: string): Observable<User> {
        this.storageService.set('client', clientId);
        const url = `/api/workspace/client/${clientId}/setClient/`;
        const options = {
            withCredentials: true,
        };
        return this.http.post<Client>(url, options).pipe(
            tap((client) => this.client.next(client)),
            mergeMap(() => {
                const token = this.storageService.get('token');
                return this.authService.login(token);
            }),
        );
    }

    create(token: string): Observable<Client> {
        const url = `/api/workspace/client/signup/`;
        const options = {
            token,
            withCredentials: true,
        };
        return this.http.post(url, options).pipe(map((client) => deserialize(Client, client)));
    }

    delete(clientId: string): Observable<User> {
        this.storageService.pop('client');
        const url = `/api/workspace/client/${clientId}/`;
        const options = {
            withCredentials: true,
        };
        return this.http.delete(url, options).pipe(
            tap(() => this.get(true)),
            mergeMap((response) => this.setClient(response['client_id'])),
        );
    }

    cancel(clientId: string): Observable<Client> {
        const url = `/api/workspace/subscription/cancel/`;
        const options = {
            withCredentials: true,
        };
        return this.http.delete(url, options).pipe(map((client) => deserialize(Client, client)));
    }

    changeStorage(clientId: string, units: number) {
        const url = `/api/workspace/subscription/updatestorage/`;
        const options = {
            units,
            withCredentials: true,
        };
        return this.http.put<null>(url, options);
    }

    removeUser(clientId: string, userId: string) {
        const url = `/api/workspace/subscription/removeuser/`;
        const options = {
            user: userId,
            withCredentials: true,
        };
        return this.http.put<null>(url, options);
    }

    transfer(clientId: string, userId: string): Observable<null> {
        const url = `/api/workspace/client/${clientId}/transfer/`;
        const options = {
            user: userId,
            withCredentials: true,
        };
        return this.http.post<null>(url, options);
    }

    createAccessToken(
        clientId: string,
        title: string,
        description: string,
        owner: string,
        expires: Date,
    ): Observable<any> {
        const url = `/api/workspace/accesstoken/`;
        const options = {
            client: clientId,
            title,
            description,
            user: owner,
            expires,
            withCredentials: true,
        };
        return this.http.post<null>(url, options);
    }

    listAccessTokens(): Observable<any> {
        const url = `/api/workspace/accesstoken/`;
        return this.http.get<null>(url);
    }

    removeAccessToken(accessTokenId: string): Observable<any> {
        const url = `/api/workspace/accesstoken/${accessTokenId}/`;
        const options = {
            withCredentials: true,
        };
        return this.http.delete(url, options);
    }
}
